import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { Button, Tooltip } from 'antd';
import { HolderOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { Flex } from 'antd';
import EditButton from './EditButton';
import styles from '../../styles/global.module.css';
import { ShapeType } from '@/types/commontypes';
import { deleteShape } from '@/utils/deleteShape';
import DeleteShapeButton from './DeleteShapeButton';
import DynamicRenderer from '../ShapeProperty/DynamicRenderer';
import { useChartShapes, useChartUIContext } from '../../contexts';
import { useGlobalShapeSelector } from '../../contexts/GlobalShapesContext';
import { useChartUIStoreSelector } from '../../stores/chartUIStore';
import DraggableWrapper from '../common/DraggableWrapper';
import { useLockShape } from '@/utils/lockShape';
import { useShapePropertyEditor } from '../../hooks/useShapePropertyEditor';

const ShapeMiniToolbar: React.FC<{}> = () => {
  const shapeTypeToDraw = useGlobalShapeSelector(s => s.shapeTypeToDraw);
  const { shapeStore } = useChartShapes();
  const renderNodes = useStore(shapeStore, s => s.renderNodes);
  const setRenderNodes = useStore(shapeStore, s => s.setRenderNodes);
  const selectedShape = useGlobalShapeSelector(s => s.selectedShape);
  const { chartUIStore } = useChartUIContext();
  const isPopupVisible = useChartUIStoreSelector(chartUIStore, s => s.isPopupVisible);
  const chartClicked = useChartUIStoreSelector(chartUIStore, s => s.chartClicked);
  const locked = useChartUIStoreSelector(chartUIStore, s => s.locked);
  const miniToolbarState = useChartUIStoreSelector(chartUIStore, s => s.miniToolbarState);
  const setMiniToolbarState = useChartUIStoreSelector(chartUIStore, s => s.setMiniToolbarState);
  const miniToolbarBoundsEl = useChartUIStoreSelector(chartUIStore, s => s.miniToolbarBoundsEl);
  const { toggleLock } = useLockShape();

  // Shared editing logic
  const { currentProps, resolveProperties, handlePropChange } = useShapePropertyEditor();

  const [isOpen, setIsOpen] = useState(!!selectedShape);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [baseClassName, setBaseClassName] = useState('');

  // Open the toolbar when a shape is selected
  useEffect(() => {
    setIsOpen(!!selectedShape);
  }, [selectedShape]);

  // Logic for opening/closing the toolbar based on shape which is drawing on click
  useEffect(() => {
    if (!selectedShape || selectedShape.mode !== 'create') return;
    if (baseClassName !== 'react-financial-charts-crosshair-cursor') return;

    const chartId = selectedShape?.chartId;
    if (!chartId) return;

    if (selectedShape.type === ShapeType.HORZ_LINE) {
      const lines = renderNodes[chartId]?.[ShapeType.HORZ_LINE] ?? [];
      const matched = lines.find((line: { uuid: string; selected?: boolean }) => line.uuid === selectedShape.uuid);
      if (matched) setIsOpen(true);
    } else if (selectedShape?.type === ShapeType.VERT_LINE) {
      const lines = renderNodes[chartId]?.[ShapeType.VERT_LINE] ?? [];
      const matched = lines.find((line: { uuid: string; selected?: boolean }) => line.uuid === selectedShape.uuid);
      if (matched) setIsOpen(true);
    }
  }, [selectedShape, baseClassName, renderNodes]);

  // Close toolbar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const target = event.target as HTMLElement;
      if (target instanceof SVGElement && target.className?.baseVal) {
        const newBase = target.className.baseVal;
        setBaseClassName(newBase);
        if (
          !isPopupVisible &&
          newBase !== 'react-financial-charts-crosshair-cursor' &&
          !(selectedShape?.mode === 'create') &&
          chartClicked &&
          selectedShape?.type === ShapeType.TEXT
        ) {
          setIsOpen(false);
        }
        return;
      }

      const popover = document.querySelector('.ant-popover');
      const isClickInsidePopover = popover?.contains(target);
      const isClickInsideToolbar = toolbarRef.current?.contains(target);
      const isClickInsidePopup = document.getElementById('shape-property-popup')?.contains(target);

      const isInDropdown = !!target.closest(
        '.ant-dropdown, .ant-select-dropdown, .ant-color-picker-dropdown, .ant-picker-dropdown',
      );

      if (isClickInsideToolbar || isClickInsidePopup || isClickInsidePopover || isInDropdown) {
        return;
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [chartClicked, isPopupVisible, selectedShape?.mode, selectedShape?.type]);

  const handleDragEnd = (newPosition: { x: number; y: number }) => {
    setMiniToolbarState(prev => ({ ...prev, x: newPosition.x, y: newPosition.y }));
  };

  // Re-measure when toolbar opens or shape type changes
  useLayoutEffect(() => {
    if (!isOpen || !toolbarRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const toolbarElement = toolbarRef.current.firstElementChild as HTMLElement;
    if (!toolbarElement) return;

    const { offsetWidth: width, offsetHeight: height } = toolbarElement;
    if (width > 0 && height > 0) {
      setMiniToolbarState(prev => {
        if (prev.width !== width || prev.height !== height) {
          return { ...prev, width, height };
        }
        return prev;
      });
    }
  }, [isOpen, selectedShape?.type, shapeTypeToDraw, setMiniToolbarState]);

  const onDelete = () => {
    if (!selectedShape) return;
    const { uuid, chartId, type } = selectedShape;
    if (!uuid || !chartId || !type) return;
    setRenderNodes(prev => deleteShape(uuid, prev, chartId, type));
    shapeStore.getState().markDeleted(uuid);
  };

  const shape = selectedShape?.type ?? shapeTypeToDraw;

  if (!shape || shape === ShapeType.ORDER_LINE || shape === ShapeType.MEASURE) return null;

  const miniProperties = resolveProperties(shape, true);

  return (
    <>
      {isOpen && (
        <DraggableWrapper
          position={miniToolbarState}
          onDragEnd={handleDragEnd}
          boundsRef={miniToolbarBoundsEl}
          handleClass="drag-handle"
          innerRef={toolbarRef}
          style={{ zIndex: 1000 }}
        >
          <Flex wrap={false} gap="small" className={`${styles.draggable_widget}`}>
            <div className={`${styles.drag_handle} drag-handle flex items-center justify-center mr-1`}>
              <HolderOutlined className="text-xl cursor-grab" />
            </div>
            <div className="">
              <DynamicRenderer
                properties={miniProperties}
                shapeProps={currentProps ?? {}}
                onPropChange={handlePropChange}
                type={shape}
                isFromMiniToolbar={true}
              />
            </div>
            <EditButton />
            <Tooltip title={locked ? 'Unlock' : 'Lock'}>
              <Button
                icon={locked ? <LockOutlined style={{ color: '#ffffff' }} /> : <UnlockOutlined />}
                onClick={() =>
                  selectedShape && toggleLock(selectedShape.chartId, selectedShape.type, selectedShape.uuid)
                }
                style={{
                  backgroundColor: locked ? '#1890ff' : undefined,
                  borderColor: locked ? '#ffffff' : undefined,
                }}
              />
            </Tooltip>
            <DeleteShapeButton onDelete={onDelete} locked={locked} />
          </Flex>
        </DraggableWrapper>
      )}
    </>
  );
};

export default ShapeMiniToolbar;
